import React from 'react';
import { Modal, Space, Tag, Button, Alert, Table, Typography, Popconfirm } from 'antd';
import { WarningOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatCurrency } from './journalEntriesHelpers';

const { Text } = Typography;

export interface PendingMovementsModalProps {
  visible: boolean;
  onClose: () => void;
  pendingMovements: any[];
  loading: boolean;
  reprocessing: boolean;
  lockDate?: string | null;
  onReprocessAll: () => Promise<void>;
  onProcessSingle: (record: any, targetDate?: string) => Promise<void>;
}

export const PendingMovementsModal: React.FC<PendingMovementsModalProps> = ({
  visible,
  onClose,
  pendingMovements,
  loading,
  reprocessing,
  lockDate,
  onReprocessAll,
  onProcessSingle,
}) => {
  return (
    <Modal
      title={
        <Space>
          <WarningOutlined style={{ color: '#ff4d4f' }} />
          <span>Movimientos Pendientes o Fallidos de Contabilización</span>
          <Tag color="error">{pendingMovements.length}</Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
        <Button
          key="reprocess"
          type="primary"
          icon={<ReloadOutlined spin={reprocessing} />}
          loading={reprocessing}
          onClick={onReprocessAll}
        >
          Reprocesar Todos los Pendientes
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Alert
          type="warning"
          showIcon
          message="Movimientos sin comprobante contable"
          description="Estos documentos fueron registrados operativamente pero no generaron comprobante contable (por ejemplo, porque la fecha coincidía con un periodo contable cerrado o faltaba configuración de cuentas). Si el periodo fue abierto o regularizado, haga clic en 'Contabilizar' o 'Reprocesar'."
        />

        <Table
          dataSource={pendingMovements}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 5 }}
          columns={[
            {
              title: 'Documento',
              dataIndex: 'document_number',
              key: 'document_number',
              render: (num: string, r: any) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{num || `#${r.id}`}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {r.document_type}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Fecha Doc.',
              dataIndex: 'document_date',
              key: 'document_date',
              render: (d: string, r: any) => {
                const dateStr = d || r.created_at;
                const formatted = dateStr ? dayjs(dateStr).format('DD/MM/YYYY') : '-';
                const isLocked =
                  lockDate && dateStr && dateStr.substring(0, 10) <= lockDate;
                return (
                  <Space direction="vertical" size={2}>
                    <Text>{formatted}</Text>
                    {isLocked && (
                      <Tag color="error" style={{ fontSize: 10, margin: 0 }}>
                        Periodo cerrado ({dayjs(lockDate).format('DD/MM/YYYY')})
                      </Tag>
                    )}
                  </Space>
                );
              },
            },
            {
              title: 'Total',
              dataIndex: 'total_amount',
              key: 'total_amount',
              render: (val: number, r: any) =>
                formatCurrency(val || r.total_amount_net || 0),
            },
            {
              title: 'Estado Contable',
              dataIndex: 'accounting_status',
              key: 'accounting_status',
              render: (st: string) => {
                let color = 'warning';
                let label = 'PENDIENTE';
                if (st === 'FAILED') {
                  color = 'error';
                  label = 'FALLIDO';
                } else if (st === 'NOT_APPLICABLE' || st === 'IGNORED') {
                  color = 'default';
                  label = 'NO APLICA';
                }
                return <Tag color={color}>{label}</Tag>;
              },
            },
            {
              title: 'Acción',
              key: 'action',
              render: (_: any, r: any) => {
                const dateStr = r.document_date || r.created_at;
                const isLocked =
                  lockDate && dateStr && dateStr.substring(0, 10) <= lockDate;
                return (
                  <Space direction="vertical" size={4}>
                    <Button
                      size="small"
                      type={isLocked ? 'default' : 'primary'}
                      icon={<SyncOutlined />}
                      loading={reprocessing}
                      onClick={() => onProcessSingle(r)}
                    >
                      Contabilizar
                    </Button>
                    {isLocked && (() => {
                      const targetDate =
                        lockDate && dayjs().format('YYYY-MM-DD') <= lockDate
                          ? dayjs(lockDate).add(1, 'day').format('YYYY-MM-DD')
                          : dayjs().format('YYYY-MM-DD');
                      const targetFormatted = dayjs(targetDate).format('DD/MM/YYYY');

                      return (
                        <Popconfirm
                          title="¿Causar en periodo abierto?"
                          description={
                            <div style={{ maxWidth: 300 }}>
                              <p style={{ margin: '0 0 6px 0' }}>
                                La fecha original del documento (
                                <strong>{dayjs(dateStr).format('DD/MM/YYYY')}</strong>)
                                se encuentra en un periodo cerrado (hasta{' '}
                                {dayjs(lockDate).format('DD/MM/YYYY')}).
                              </p>
                              <p style={{ margin: 0, color: '#1677ff' }}>
                                Se contabilizará con fecha{' '}
                                <strong>{targetFormatted}</strong> (fecha actual de
                                hoy o primera fecha contablemente habilitada).
                              </p>
                            </div>
                          }
                          okText="Sí, causar"
                          cancelText="Cancelar"
                          placement="topLeft"
                          onConfirm={() => onProcessSingle(r, targetDate)}
                        >
                          <Button
                            size="small"
                            type="dashed"
                            style={{ fontSize: 11 }}
                            loading={reprocessing}
                          >
                            Causar en Periodo Abierto
                          </Button>
                        </Popconfirm>
                      );
                    })()}
                  </Space>
                );
              },
            },
          ]}
        />
      </div>
    </Modal>
  );
};
